/**
 * One-time hero moment: decorative one-shot reveals may exceed the 400 ms
 * in-app cap. Interactive states (hover, press, focus, button fades, exit)
 * stay at or under 400 ms. The real room underneath is never animated.
 *
 * Choreography: pitch black with a single start button. The click is the one
 * clock for visuals and sound: the button dissolves, a yellow ember grows,
 * the mark pulls into focus from a soft blur and lands exactly on the light
 * burst and the chime hit. Only then the words appear. Every layer sits on a
 * fixed stage, so nothing reflows while it animates.
 */
import { LogoMark } from "@/components/common/Logo/LogoMark"
import { Typography } from "@/components/common/Typography/Typography"
import { Button } from "@/components/ui/button"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import type { DataRoomFile } from "@/types/dataRoom"
import * as Dialog from "@radix-ui/react-dialog"
import { ArrowRight, Volume2, VolumeX } from "lucide-react"
import { AnimatePresence, useIsPresent, useReducedMotion } from "motion/react"
import * as m from "motion/react-m"
import { forwardRef, useEffect, useLayoutEffect, useRef, useState } from "react"
import { useWelcomeSound } from "./useWelcomeSound"
import { WELCOME_MOTION as T } from "./welcomeMotion"

interface DataRoomWelcomeProps {
  open: boolean
  sound: boolean
  checklist: DataRoomFile | null
  onToggleSound: () => void
  onDismiss: () => void
  onAfterClose: (openChecklist: boolean) => void
}

type Stage = "idle" | "ignite" | "landed" | "ready"

const SURFACE = "#0B0B0D"
const FLASH =
  "radial-gradient(circle at 50% 40%, rgba(255,248,176,0.95), rgba(255,248,176,0.35) 35%, rgba(255,248,176,0) 75%)"
const EMBER =
  "radial-gradient(closest-side, rgba(255,244,64,0.55), rgba(255,244,64,0.18) 45%, rgba(255,244,64,0) 70%)"

interface WelcomeSceneProps {
  children: React.ReactNode
  reduced: boolean
  second: boolean
}

const WelcomeScene: React.FC<WelcomeSceneProps> = ({
  children,
  reduced,
  second,
}) => {
  const present = useIsPresent()
  return (
    <m.div
      aria-hidden={!present || undefined}
      ref={(element) => {
        if (!element) return
        if (present) element.removeAttribute("inert")
        else element.setAttribute("inert", "")
      }}
      initial={second ? { opacity: 0 } : false}
      animate={{ opacity: 1, y: 0 }}
      exit={reduced ? { opacity: 0 } : { opacity: 0, y: -16 }}
      transition={{ duration: reduced ? T.reduced : T.sceneExit, ease: T.ease }}
    >
      {children}
    </m.div>
  )
}

interface StartButtonProps {
  reduced: boolean
  onClick: () => void
  children: React.ReactNode
}

const RING =
  "conic-gradient(from 0deg, rgba(255,244,64,0) 0deg, rgba(255,244,64,0) 250deg, rgba(255,252,202,0.95) 320deg, rgba(255,244,64,0) 360deg)"
const START_HALO =
  "radial-gradient(closest-side, rgba(255,244,64,0.28), rgba(255,244,64,0.08) 55%, rgba(255,244,64,0) 75%)"
const SHEEN =
  "linear-gradient(100deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.65) 50%, rgba(255,255,255,0) 100%)"

/**
 * The only thing on the black screen, so it carries the whole first
 * impression: it pulls in from a soft blur, a light edge orbits the pill, a
 * sheen glides across it now and then, and a halo breathes behind it. No
 * opacity on the entrance, so the button is operable from the first frame.
 */
const StartButton = forwardRef<HTMLButtonElement, StartButtonProps>(
  ({ reduced, onClick, children }, ref) => (
    <m.div
      className="relative flex items-center justify-center"
      initial={reduced ? false : { scale: 0.88, filter: "blur(10px)" }}
      animate={{ scale: 1, filter: "blur(0px)" }}
      transition={{ duration: T.startIn, ease: T.ease }}
    >
      {!reduced && (
        <m.div
          aria-hidden
          className="pointer-events-none absolute h-[320px] w-[320px] max-w-none"
          style={{ background: START_HALO }}
          animate={{ scale: [0.9, 1.08, 0.9], opacity: [0.55, 1, 0.55] }}
          transition={{
            duration: T.breathe,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      )}
      <m.div
        className="relative overflow-hidden rounded-full p-[1.5px]"
        whileHover={reduced ? undefined : { scale: 1.04 }}
        whileTap={reduced ? undefined : { scale: 0.97 }}
        transition={{ duration: 0.2, ease: T.ease }}
      >
        {!reduced && (
          <m.div
            aria-hidden
            className="pointer-events-none absolute h-[260px] w-[260px]"
            style={{
              left: "50%",
              top: "50%",
              x: "-50%",
              y: "-50%",
              background: RING,
            }}
            animate={{ rotate: 360 }}
            transition={{ duration: T.orbit, repeat: Infinity, ease: T.linear }}
          />
        )}
        <button
          ref={ref}
          type="button"
          onClick={onClick}
          className="relative h-12 overflow-hidden rounded-full bg-yellow1 px-8 text-[16px] font-semibold text-black shadow-[0_0_28px_rgba(255,244,64,0.25)] transition-shadow duration-200 hover:shadow-[0_0_44px_rgba(255,244,64,0.45)] focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-black"
        >
          {!reduced && (
            <m.span
              aria-hidden
              className="pointer-events-none absolute inset-y-0 left-0 w-1/2"
              style={{ background: SHEEN }}
              initial={{ x: "-120%" }}
              animate={{ x: "320%" }}
              transition={{
                duration: T.sheen,
                repeat: Infinity,
                repeatDelay: T.sheenPause,
                delay: T.startIn,
                ease: T.ease,
              }}
            />
          )}
          <span className="relative">{children}</span>
        </button>
      </m.div>
    </m.div>
  ),
)
StartButton.displayName = "StartButton"

interface IgniteMarkProps {
  reduced: boolean
  landed: boolean
}

/** The mark pulls into focus from a soft blur, then glows once on landing. */
const IgniteMark: React.FC<IgniteMarkProps> = ({ reduced, landed }) => {
  if (reduced) {
    return (
      <m.svg
        data-testid="welcome-mark"
        width="160"
        height="119"
        viewBox="0 0 81 60"
        className="relative text-yellow1"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2 }}
      >
        <LogoMark />
      </m.svg>
    )
  }
  return (
    <m.div
      data-testid="welcome-mark"
      className="relative h-[119px] w-[160px]"
      initial={{ opacity: 0, scale: 1.6, filter: "blur(24px)" }}
      animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
      transition={{
        opacity: { duration: T.mark * 0.5, delay: T.markDelay, ease: T.ease },
        scale: { duration: T.mark, delay: T.markDelay, ease: T.ease },
        filter: { duration: T.mark, delay: T.markDelay, ease: T.ease },
      }}
    >
      {/* Glow lives on its own layer so it never interpolates against the blur. */}
      <m.div
        className="absolute inset-0 text-yellow1"
        initial={{ filter: "drop-shadow(0 0 0px rgba(255,244,64,0))" }}
        animate={
          landed
            ? {
                filter: [
                  "drop-shadow(0 0 0px rgba(255,244,64,0))",
                  "drop-shadow(0 0 32px rgba(255,244,64,0.7))",
                  "drop-shadow(0 0 14px rgba(255,244,64,0.3))",
                ],
              }
            : undefined
        }
        transition={{ duration: T.glowLand, times: [0, 0.25, 1], ease: T.ease }}
      >
        <LogoMark />
      </m.div>
    </m.div>
  )
}

interface EmberProps {
  reduced: boolean
  landed: boolean
}

/** Grows with the rumble, settles into a breathing halo after the flash. */
const Ember: React.FC<EmberProps> = ({ reduced, landed }) => {
  const [breathing, setBreathing] = useState(false)
  if (reduced) return null
  return (
    <m.div
      className="pointer-events-none absolute h-[720px] w-[720px] max-w-none"
      style={{ background: EMBER }}
      initial={{ scale: 0.01, opacity: 0 }}
      animate={
        breathing
          ? { scale: [0.5, 0.525, 0.5], opacity: 0.7 }
          : landed
            ? { scale: 0.5, opacity: 0.7 }
            : { scale: 1, opacity: 1 }
      }
      transition={
        breathing
          ? {
              scale: { duration: T.haloLoop, repeat: Infinity, ease: T.linear },
              opacity: { duration: 0 },
            }
          : landed
            ? { duration: T.afterglow, ease: T.ease }
            : {
                scale: {
                  duration: T.ember,
                  delay: T.emberDelay,
                  ease: T.emberEase,
                },
                opacity: { duration: 0.3, delay: T.emberDelay },
              }
      }
      onAnimationComplete={() => {
        if (landed && !breathing) setBreathing(true)
      }}
    />
  )
}

interface WelcomeContentProps
  extends Omit<DataRoomWelcomeProps, "onAfterClose"> {
  /** Fires once after Radix released focus; the outer component decides what to open. */
  onAfterClose: () => void
  reduced: boolean
  play: () => void
  cancel: () => void
  finish: (checklist: boolean) => void
}

const WelcomeContent: React.FC<WelcomeContentProps> = ({
  sound,
  checklist,
  onToggleSound,
  onAfterClose,
  reduced,
  play,
  cancel,
  finish,
}) => {
  const { translate } = useTranslation()
  const [scene, setScene] = useState<1 | 2>(1)
  const [stage, setStage] = useState<Stage>("idle")
  const [ignited, setIgnited] = useState(false)
  const [flashVisible, setFlashVisible] = useState(false)
  const [titleVisible, setTitleVisible] = useState(false)
  const [secondVisible, setSecondVisible] = useState(false)
  const primaryRef = useRef<HTMLButtonElement>(null)
  const nextRef = useRef<HTMLButtonElement>(null)
  const liveRef = useRef<HTMLDivElement>(null)
  const headingRef = useRef<HTMLHeadingElement>(null)
  const started = useRef(false)
  const landed = stage === "landed" || stage === "ready"

  // The click starts one timeline; every beat below is relative to it. Keyed
  // on the one-shot flag, not on the stage, so a beat never clears the next.
  useEffect(() => {
    if (!ignited) return
    const at = (seconds: number, run: () => void) =>
      setTimeout(run, reduced ? 0 : seconds * 1000)
    const timers = [
      at(T.flashAt, () => {
        setStage("landed")
        if (!reduced) setFlashVisible(true)
      }),
      at(T.flashAt + T.flashIn + T.flashOut, () => setFlashVisible(false)),
      at(T.titleAt, () => setTitleVisible(true)),
      at(T.nextAt, () => setStage("ready")),
    ]
    return () => timers.forEach(clearTimeout)
  }, [ignited, reduced])

  useLayoutEffect(() => {
    if (stage === "ready") nextRef.current?.focus()
  }, [stage])

  useEffect(() => {
    if (scene !== 2 || reduced) return
    const timer = setTimeout(
      () => setSecondVisible(true),
      (T.sceneExit + T.sceneGap) * 1000,
    )
    return () => clearTimeout(timer)
  }, [scene, reduced])

  useLayoutEffect(() => {
    if (secondVisible) headingRef.current?.focus()
  }, [secondVisible])

  const dismiss = (openChecklist = false) => {
    cancel()
    finish(openChecklist)
  }
  const start = () => {
    if (started.current) return
    started.current = true
    if (sound) play()
    setStage("ignite")
    setIgnited(true)
    liveRef.current?.focus()
  }
  const controlClass =
    "text-grey5 opacity-55 transition-opacity hover:opacity-100 focus-visible:opacity-100"

  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open) dismiss()
      }}
    >
      <Dialog.Portal forceMount>
        <Dialog.Content
          forceMount
          asChild
          onOpenAutoFocus={(event) => {
            event.preventDefault()
            primaryRef.current?.focus()
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault()
            onAfterClose()
          }}
          onPointerDownOutside={(event) => event.preventDefault()}
        >
          <m.div
            className="fixed inset-0 z-50 flex flex-col overflow-y-auto overscroll-contain text-white"
            style={{ background: SURFACE }}
            initial={false}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              duration: reduced ? T.reduced : T.exit,
              ease: T.ease,
            }}
          >
            {flashVisible && (
              <m.div
                aria-hidden
                data-testid="welcome-flash"
                className="pointer-events-none fixed inset-0 z-20"
                style={{ background: FLASH }}
                initial={{ opacity: 0 }}
                animate={{ opacity: [0, 1, 0] }}
                transition={{
                  duration: T.flashIn + T.flashOut,
                  times: [0, T.flashIn / (T.flashIn + T.flashOut), 1],
                  ease: "easeOut",
                }}
              />
            )}
            <div className="my-auto w-full py-8">
              {/* The one dialog title/description are stable while the visual scenes change. */}
              <Dialog.Title className="sr-only">
                {translate("DATA_ROOM/WELCOME/TITLE")}
              </Dialog.Title>
              <Dialog.Description className="sr-only">
                {translate("DATA_ROOM/WELCOME/BODY")}
              </Dialog.Description>
              {/* Focus parks here between the click and the next button so Tab still reaches the controls. */}
              <div ref={liveRef} tabIndex={-1} className="sr-only" />
              <AnimatePresence
                initial={false}
                onExitComplete={() => {
                  // Reduced motion has no inter-scene gap. Wait for removal
                  // so both scenes never occupy the scrolling layout at once.
                  if (reduced && scene === 2) setSecondVisible(true)
                }}
              >
                {scene === 1 && (
                  <WelcomeScene key="welcome" second={false} reduced={reduced}>
                    {/* Fixed stage: every layer is absolutely placed, so the
                        mark never moves when the button leaves or the words arrive. */}
                    <div className="relative mx-auto h-[440px] w-full max-w-xl px-6 text-center">
                      {stage !== "idle" && (
                        <div
                          className="absolute inset-x-0 top-0 flex h-[240px] items-center justify-center"
                          aria-hidden
                        >
                          <Ember reduced={reduced} landed={landed} />
                          <IgniteMark reduced={reduced} landed={landed} />
                        </div>
                      )}
                      <AnimatePresence>
                        {stage === "idle" && (
                          <m.div
                            key="start"
                            className="absolute inset-0 flex items-center justify-center"
                            exit={
                              reduced
                                ? { opacity: 0 }
                                : { opacity: 0, scale: 0.92 }
                            }
                            transition={{
                              duration: reduced ? T.reduced : T.buttonOut,
                              ease: T.ease,
                            }}
                          >
                            <StartButton
                              ref={primaryRef}
                              reduced={reduced}
                              onClick={start}
                            >
                              {translate("DATA_ROOM/WELCOME/START")}
                            </StartButton>
                          </m.div>
                        )}
                      </AnimatePresence>
                      <div className="absolute inset-x-0 top-[248px] px-6">
                        {titleVisible && (
                          <>
                            <m.div
                              data-testid="welcome-title"
                              initial={
                                reduced ? { opacity: 0 } : { opacity: 0, y: 14 }
                              }
                              animate={{ opacity: 1, y: 0 }}
                              transition={{
                                duration: reduced ? T.reduced : T.words,
                                ease: T.ease,
                              }}
                            >
                              <Typography as="h2" variant="titleL">
                                {translate("DATA_ROOM/WELCOME/TITLE")}
                              </Typography>
                            </m.div>
                            <m.div
                              className="mt-4"
                              initial={
                                reduced ? { opacity: 0 } : { opacity: 0, y: 14 }
                              }
                              animate={{ opacity: 1, y: 0 }}
                              transition={{
                                duration: reduced ? T.reduced : T.words,
                                delay: reduced ? 0 : T.bodyAt - T.titleAt,
                                ease: T.ease,
                              }}
                            >
                              <Typography
                                variant="textS"
                                className="text-grey5"
                              >
                                {translate("DATA_ROOM/WELCOME/BODY")}
                              </Typography>
                            </m.div>
                          </>
                        )}
                        {stage === "ready" && (
                          <m.div
                            className="mt-8"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{
                              duration: reduced ? T.reduced : T.next,
                            }}
                          >
                            <Button ref={nextRef} onClick={() => setScene(2)}>
                              {translate("DATA_ROOM/WELCOME/NEXT")}
                            </Button>
                          </m.div>
                        )}
                      </div>
                    </div>
                  </WelcomeScene>
                )}
              </AnimatePresence>
              {secondVisible && (
                <WelcomeScene second reduced={reduced}>
                  <div className="mx-auto flex w-full max-w-[760px] flex-col items-center px-6 sm:px-10">
                    <m.div
                      data-testid="welcome-ready-title"
                      className="w-full text-center"
                      initial={reduced ? { opacity: 0 } : { opacity: 0, y: 12 }}
                      animate={reduced ? { opacity: 1 } : { opacity: 1, y: 0 }}
                      transition={{
                        duration: reduced ? T.reduced : T.readyTitle,
                        ease: T.ease,
                      }}
                    >
                      <h2
                        ref={headingRef}
                        tabIndex={-1}
                        className="rounded outline-hidden"
                      >
                        <Typography
                          as="span"
                          variant="titleL"
                          className="text-balance tracking-[-0.045em]"
                          style={{ fontSize: "clamp(2rem, 3.4vw, 2.75rem)" }}
                        >
                          {translate("DATA_ROOM/WELCOME/READY")}
                        </Typography>
                      </h2>
                    </m.div>
                    <ol
                      className="mt-10 w-full max-w-[640px] text-left sm:mt-12"
                      data-testid="welcome-steps"
                    >
                      {(
                        [
                          [
                            "DATA_ROOM/WELCOME/STEP_CHECKLIST",
                            "DATA_ROOM/WELCOME/STEP_CHECKLIST_DETAIL",
                          ],
                          [
                            "DATA_ROOM/WELCOME/STEP_UPLOAD",
                            "DATA_ROOM/WELCOME/STEP_UPLOAD_DETAIL",
                          ],
                          [
                            "DATA_ROOM/WELCOME/STEP_REVIEW",
                            "DATA_ROOM/WELCOME/STEP_REVIEW_DETAIL",
                          ],
                        ] as const
                      ).map(([key, detail], index) => {
                        const at = T.readyStepDelay + index * T.readyStepStagger
                        return (
                          <li
                            key={key}
                            className="relative pb-9 pl-8 last:pb-0 sm:pb-10 sm:pl-10"
                          >
                            {/* Each segment spans its row, so the light reaches
                                the next node even when translated copy wraps. */}
                            {index < 2 && (
                              <div
                                aria-hidden
                                className="pointer-events-none absolute left-[3.5px] top-7 h-full w-px bg-white/10"
                              >
                                {!reduced && (
                                  <m.div
                                    data-testid="welcome-rail-segment"
                                    className="absolute inset-0 origin-top bg-yellow1/50"
                                    initial={{ scaleY: 0 }}
                                    animate={{ scaleY: 1 }}
                                    transition={{
                                      duration: T.readyStepStagger,
                                      delay: at,
                                      ease: T.ease,
                                    }}
                                  />
                                )}
                              </div>
                            )}
                            <div
                              aria-hidden
                              className="pointer-events-none absolute left-0 top-6 flex h-2 w-2 items-center justify-center"
                            >
                              {!reduced && (
                                <m.div
                                  data-testid="welcome-step-glow"
                                  className="absolute h-[72px] w-[72px] max-w-none"
                                  style={{ background: START_HALO }}
                                  initial={{ opacity: 0, scale: 0.6 }}
                                  animate={{
                                    opacity: [0, 1, 0],
                                    scale: [0.6, 1, 0.8],
                                  }}
                                  transition={{
                                    duration: T.readyGlint,
                                    delay: at,
                                    times: [0, 0.35, 1],
                                    ease: T.ease,
                                  }}
                                />
                              )}
                              <m.span
                                className="relative h-2 w-2 rounded-full bg-yellow1"
                                initial={
                                  reduced
                                    ? { opacity: 0 }
                                    : { opacity: 0, scale: 0.65 }
                                }
                                animate={
                                  reduced
                                    ? { opacity: 1 }
                                    : { opacity: 1, scale: 1 }
                                }
                                transition={{
                                  duration: reduced ? T.reduced : T.readyNode,
                                  delay: reduced ? 0 : at,
                                  ease: T.ease,
                                }}
                              />
                            </div>
                            <m.div
                              data-testid="welcome-step-content"
                              className="grid grid-cols-[48px_minmax(0,1fr)] items-start gap-4 sm:grid-cols-[64px_minmax(0,1fr)] sm:gap-7"
                              initial={
                                reduced ? { opacity: 0 } : { opacity: 0, x: 12 }
                              }
                              animate={
                                reduced ? { opacity: 1 } : { opacity: 1, x: 0 }
                              }
                              transition={{
                                duration: reduced ? T.reduced : T.readyStep,
                                delay: reduced ? 0 : at,
                                ease: T.ease,
                              }}
                            >
                              <span aria-hidden>
                                <Typography
                                  as="span"
                                  variant="titleL"
                                  className="block font-light tabular-nums tracking-[-0.07em] text-white/35"
                                  style={{
                                    fontSize: "clamp(2.5rem, 4vw, 3.5rem)",
                                    lineHeight: 1,
                                  }}
                                >
                                  0{index + 1}
                                </Typography>
                              </span>
                              <div className="min-w-0 pt-1">
                                <Typography
                                  as="h3"
                                  variant="textM"
                                  className="text-pretty font-medium leading-relaxed! sm:text-[19px]!"
                                >
                                  {translate(key)}
                                </Typography>
                                <Typography
                                  variant="textS"
                                  className="mt-2 text-pretty leading-relaxed! text-grey5"
                                >
                                  {translate(detail)}
                                </Typography>
                              </div>
                            </m.div>
                          </li>
                        )
                      })}
                    </ol>
                    <m.div
                      data-testid="welcome-ready-action"
                      className="relative mt-10 flex max-w-full items-center justify-center sm:mt-12"
                      initial={reduced ? { opacity: 0 } : { opacity: 0, y: 8 }}
                      animate={reduced ? { opacity: 1 } : { opacity: 1, y: 0 }}
                      transition={{
                        duration: reduced ? T.reduced : T.readyAction,
                        delay: reduced ? 0 : T.readyActionAt,
                        ease: T.ease,
                      }}
                    >
                      {!reduced && (
                        <div
                          aria-hidden
                          data-testid="welcome-action-glow"
                          className="pointer-events-none absolute h-[140px] w-[320px] max-w-none"
                          style={{ background: START_HALO }}
                        />
                      )}
                      <m.button
                        type="button"
                        onClick={() => dismiss(Boolean(checklist))}
                        className="relative inline-flex min-h-14 max-w-full items-center justify-center gap-4 rounded-full border border-white/40 bg-yellow1 px-7 py-4 text-left focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4 focus-visible:ring-offset-[#0B0B0D]"
                        style={{
                          boxShadow: reduced
                            ? undefined
                            : "inset 0 1px 0 rgba(255,255,255,0.65), 0 0 28px rgba(255,244,64,0.18)",
                        }}
                        whileHover={
                          reduced ? undefined : { y: -2, scale: 1.01 }
                        }
                        whileTap={reduced ? undefined : { scale: 0.98 }}
                        transition={{
                          duration: T.readyInteraction,
                          ease: T.ease,
                        }}
                      >
                        {!reduced && (
                          <span
                            aria-hidden
                            className="pointer-events-none absolute inset-0 overflow-hidden rounded-full"
                          >
                            <m.span
                              data-testid="welcome-action-sheen"
                              className="absolute inset-y-0 left-0 w-1/2"
                              style={{ background: SHEEN }}
                              initial={{ x: "-120%" }}
                              animate={{ x: "320%" }}
                              transition={{
                                duration: T.readySheen,
                                delay: T.readySheenAt,
                                ease: T.ease,
                              }}
                            />
                          </span>
                        )}
                        <Typography
                          as="span"
                          variant="textM"
                          className="relative font-semibold text-black"
                        >
                          {translate(
                            checklist
                              ? "DATA_ROOM/WELCOME/STEP_CHECKLIST"
                              : "DATA_ROOM/WELCOME/OPEN_ROOM",
                          )}
                        </Typography>
                        <ArrowRight
                          size={18}
                          strokeWidth={1.5}
                          aria-hidden
                          className="relative shrink-0 text-black"
                        />
                      </m.button>
                    </m.div>
                  </div>
                </WelcomeScene>
              )}
            </div>
            {/* Persistent controls: reachable at every moment, muted until hovered or focused. */}
            <div
              className="sticky bottom-0 z-10 flex shrink-0 items-center justify-center p-6"
              style={{ background: SURFACE }}
            >
              <Button
                variant="ghost"
                className={controlClass}
                onClick={() => dismiss()}
              >
                {translate("DATA_ROOM/WELCOME/SKIP")}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className={`absolute right-6 min-h-11 min-w-11 ${controlClass}`}
                aria-label={translate(
                  sound
                    ? "DATA_ROOM/WELCOME/SOUND_OFF"
                    : "DATA_ROOM/WELCOME/SOUND_ON",
                )}
                onClick={() => {
                  if (sound) cancel()
                  onToggleSound()
                }}
              >
                {sound ? (
                  <Volume2 size={20} strokeWidth={1.5} aria-hidden />
                ) : (
                  <VolumeX size={20} strokeWidth={1.5} aria-hidden />
                )}
              </Button>
            </div>
          </m.div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

export const DataRoomWelcome: React.FC<DataRoomWelcomeProps> = (props) => {
  const reduced = Boolean(useReducedMotion())
  const soundEngine = useWelcomeSound(props.open)
  const openChecklist = useRef(false)
  const afterClose = useRef(props.onAfterClose)
  afterClose.current = props.onAfterClose
  // Radix can report the close focus event more than once for a single
  // dismissal (Escape plus focus scope unmount). The handoff opens the
  // checklist preview, so it must run exactly once per opening.
  const afterCloseFired = useRef(false)
  useEffect(() => {
    if (props.open) {
      openChecklist.current = false
      afterCloseFired.current = false
    }
  }, [props.open])
  return (
    <AnimatePresence>
      {props.open && (
        <WelcomeContent
          key="data-room-welcome"
          {...props}
          onAfterClose={() => {
            if (afterCloseFired.current) return
            afterCloseFired.current = true
            afterClose.current(openChecklist.current)
          }}
          reduced={reduced}
          play={soundEngine.play}
          cancel={soundEngine.cancel}
          finish={(checklist) => {
            openChecklist.current = checklist
            props.onDismiss()
          }}
        />
      )}
    </AnimatePresence>
  )
}
